<template>
    <simple-layout>
        <basic-card>
            <template #header>
                {{ $t('market.interestRates') }}
            </template>

            <!--  1.level          -->
            <data-table
              :data="interestRates"
              :configuration="interestRateTableConfig"
              @focusedChanged="handleInterestRateFocus"
              component-id="interest-rate-table"
            >
                <template v-if="focusedInterestRate">
                    <basic-card>
                        <template #header>Detail - {{ focusedInterestRate.name }}</template>

                        <toolbox>
                            <toolbox-button :help-text="`${$t('searchQuery.exportExcel')}`"
                                            @press="$modal.show('exportIntervalModal')">
                                <icon :icon="['fal', 'file-excel']" size="lg"/>
                            </toolbox-button>
                        </toolbox>


                        <form-wrapper :form-title="$t('detail')">
                            <form-text-input v-model="focusedInterestRate.name" :caption="`${$t('name')}`"/>
                            <form-select-input :caption="`${$t('cashEvents.currency')}`"
                                               :options="currenciesOptions"
                                               v-model="focusedInterestRate.currencyId"/>
                        </form-wrapper>

                        <!--2. level-->
                        <framed-content :title="$t('gridPoints')">

                            <data-table
                              :data="focusedInterestRate.gridPoints"
                              :configuration="gridPointsTableConfig"
                              @add="gridPointAdd"
                              @delete="gridPointDelete"
                              @focusedChanged="handleGridPointFocus"
                              component-id="operation-definition-parameters-list"
                            >
                                <template v-if="focusedGridPoint">
                                    <basic-card>
                                        <form-wrapper>
                                            <form-text-input v-model="focusedGridPoint.name"
                                                             :caption="`${$t('name')}`"/>
                                            <form-text-input v-model="focusedGridPoint.term" :caption="$t('term')"/>
                                            <simple-button @press="handleSaveGridPoint">
                                                {{ $t('save') }}
                                            </simple-button>
                                        </form-wrapper>
                                    </basic-card>
                                </template>
                            </data-table>
                        </framed-content>

                        <!--2. level-->
                        <framed-content :title="$t('curves')">
                            <data-table
                              :data="curves"
                              :configuration="curvesTableConfig"
                              @focusedChanged="handleCurveFocus"
                              component-id="operation-definition-parameters-list"
                            >
                                <template v-if="focusedCurve">
                                    <basic-card>
                                        <template #header>
                                            {{
                                                `${focusedInterestRate.name} - ${$moment(focusedCurve.validDate).format('DD.MM.YYYY')}`
                                            }}
                                        </template>
                                        <data-table
                                          :data="curvesValues"
                                          :configuration="curvesValuesTableConfig"
                                          @add="curveValueAdd"
                                          @delete="curveValueDelete"
                                          @focusedChanged="handleCurveValueFocus"
                                        >
                                            <template v-if="focusedCurveValue">
                                                <basic-card>
                                                    <form-wrapper>
                                                        <form-text-input v-model="focusedCurveValue.termName"
                                                                         :caption="`${$t('name')}`"/>
                                                        <form-text-input v-model="focusedCurveValue.value"
                                                                         :caption="$t('term')"/>
                                                        <simple-button @press="handleSaveCurveValue">
                                                            {{ $t('save') }}
                                                        </simple-button>
                                                    </form-wrapper>
                                                </basic-card>
                                            </template>
                                        </data-table>
                                    </basic-card>
                                </template>


                            </data-table>

                        </framed-content>


                        <simple-button confirmation-protected
                                       :protection-message="`${$t('savingIR')} ${focusedInterestRate.name}`"
                                       @proceed="handleSave">
                            {{ `${$t('save')} ${focusedInterestRate.name}` }}
                        </simple-button>
                    </basic-card>
                </template>
            </data-table>

        </basic-card>

        <v-modal name="exportIntervalModal"
                 classes="transparent-modal"
                 style=" background-color: rgba(10,10,10,0.66)">
            <basic-card style="min-height: 400px; box-shadow: none !important;">
                <form-wrapper :form-title="$t('exportInterval')">
                    <form-date-input :caption="$t('dateFrom')" v-model="exportDateFrom"/>
                    <form-date-input :caption="$t('dateThru')" v-model="exportDateThru"/>
                </form-wrapper>

                <simple-button :disabled="!exportDateFrom || !exportDateThru" @press="handleExport">{{
                        $t('startExport')
                    }}
                </simple-button>


            </basic-card>
        </v-modal>
    </simple-layout>
</template>

<script>
import SimpleLayout from "@/components/layout/SimpleLayout";
import BasicCard from "@/components/layout/BasicCard";
import DataTable from "@/components/table/DataTable";
import Axios from "axios";
import FormWrapper from "@/components/form/FormWrapper";
import FormCheckboxInput from "@/components/form/FormCheckboxInput";
import SimpleButton from "@/components/button/SimpleButton";
import FormTextInput from "@/components/form/FormTextInput";
import FormSelectInput from "@/components/form/FormSelectInput";
import { mapGetters } from "vuex";
import CollapsibleContent from "@/components/CollapsibleContent";
import FramedContent from "@/components/layout/FramedContent";
import { isInInterval } from "@/utils/date-utils";
import handleTableExport from "@/utils/excel-utils";
import Toolbox from "@/components/toolbox/Toolbox";
import ToolboxButton from "@/components/toolbox/ToolboxButton";
import FormDateInput from "@/components/form/FormDateInput";


export default {
    name: "InterestRatesView",
    components: {
        FormDateInput,
        ToolboxButton,
        Toolbox,
        FramedContent,
        CollapsibleContent,
        FormSelectInput,
        FormTextInput, SimpleButton, FormCheckboxInput, FormWrapper, DataTable, BasicCard, SimpleLayout
    },
    data: function () {
        return {
            focusedInterestRate: {},
            focusedGridPoint: null,
            focusedCurve: {},
            focusedCurveValue: null,
            //1. level            
            interestRates: [],
            //2. level
            //focusedInterestRate.gridPoints: []
            //2. level
            curves: [],
            //3.level
            //curvesValues: [],


            // Configs
            interestRateTableConfig: {
                columns: [
                    {
                        caption: this.$t('name'),
                        datum: d => d.name
                    },
                    {
                        caption: this.$t('cashEvents.currency'),
                        datum: d => d.currencyId
                    },
                ],
                allowEdit: true,
            },
            gridPointsTableConfig: {
                columns: [

                    {
                        caption: this.$t('name'),
                        datum: d => d.name
                    },
                    {
                        caption: this.$t('term'),
                        datum: d => d.term
                    },
                ],
                enableAdd: true,
                allowEdit: true,
                // actions: [
                //     // {
                //     //     name: "delete",
                //     //     icon: "trash",
                //     //     tooltip: this.$t('operationDefinition.delete')
                //     // },
                // ]
            },
            curvesTableConfig: {
                columns: [
                    {
                        caption: this.$t('market.validDate'),
                        datum: d => this.$moment(d.validDate).format('DD.MM.YYYY')
                    },
                ],
                allowEdit: true,
            },
            curvesValuesTableConfig: {
                columns: [

                    {
                        caption: this.$t('gridPoint'),
                        datum: d => d.termName
                    },

                    {
                        caption: this.$t('market.value'),
                        datum: d => d.value
                    },
                ],
                enableAdd: true,
                allowEdit: true,
            },

            exportDateFrom: null,
            exportDateThru: null,

        }
    },
    computed: {
        ...mapGetters({
            "currenciesOptions": "currencies/currenciesOptions",
        }),

        curvesValues() {
            return this.focusedCurve?.values?.map((value, index) => ({
                termName: this.focusedInterestRate?.gridPoints[index]?.name ?? 'N/A',
                value: value / 100000
            })) ?? [];
        },
    },
    methods: {

        async handleInterestRateFocus(interestRate) {
            this.focusedInterestRate = interestRate;

            if (interestRate) {
                const response = await Axios.get(`currency/api/v1/interest-rates/${interestRate.id}/curves`);
                this.curves = response.data;
            }
        },

        handleCurveFocus(curve) {
            this.focusedCurve = curve;
        },
        handleGridPointFocus(gridPoint) {
            this.focusedGridPoint = gridPoint;
        },

        gridPointAdd() {

            this.focusedGridPoint = {
                name: null,
                term: null
            }
        },

        gridPointDelete(gridPoint) {
            const index = this.focusedInterestRate.gridPoints.findIndex(gp => gp === gridPoint);
            this.focusedInterestRate.gridPoints.splice(index, 1);
        },


        handleSaveGridPoint() {
            const gridPoint = this.focusedInterestRate.gridPoints.find(gp => gp.name === this.focusedGridPoint.name);

            this.focusedGridPoint.term = Number(this.focusedGridPoint.term);
            if (gridPoint) {
                gridPoint.term = this.focusedGridPoint.term;
            } else {

                this.focusedInterestRate.gridPoints.push(this.focusedGridPoint);
                this.focusedInterestRate.gridPoints.sort((a, b) => a.term - b.term)
            }
            this.focusedGridPoint = null;
        },

        curveValueAdd() {
            this.focusedCurveValue = {
                termName: null,
                value: null
            }
        },
        curveValueDelete(curveValue) {
            const index = this.curvesValues.findIndex(gp => gp === curveValue);
            this.curvesValues.splice(index, 1);
        },
        handleCurveValueFocus(curveValue) {
            this.focusedCurveValue = curveValue;
        },

        async handleSaveCurveValue() {
            const curveValue = this.curvesValues.find(cv => cv.termName === this.focusedCurveValue.termName);
            if (curveValue) {
                curveValue.value = this.focusedCurveValue.value;
            } else {
                this.curvesValues.push(this.focusedCurveValue);
                // this.curvesValues.sort((a, b) => a.termName - b.termName)
            }
            this.focusedCurveValue = null;

            const curveToSave = this.$_.cloneDeep(this.focusedCurve);
            curveToSave.values = this.curvesValues.map(cv => cv.value * 100000);

            const response = await Axios.post(`currency/api/v1/interest-rates/${this.focusedInterestRate.id}/curves`, curveToSave);


        },

        async handleSave() {


            try {
                const response = await Axios.put(`currency/api/v1/interest-rates/${this.focusedInterestRate.id}`, this.focusedInterestRate);
                this.$notify({
                    group: 'application',
                    title: this.$t('interestRates.saveSuccess'),
                    text: this.$t('interestRates.saveSuccess'),
                    type: "ok"
                })


            } catch (e) {
                this.$notify({
                    group: 'application',
                    title: this.$t('interestRates.saveError'),
                    text: this.$t('interestRates.saveError'),
                    type: "error"
                })
            }

        },
        async handleExport() {

            try {
                const filteredCurves = this.$_.cloneDeep(this.curves).filter(isInInterval('validDate', this.exportDateFrom, this.exportDateThru));

                const exportedData = filteredCurves.flatMap(curve => {
                    return curve.values.map((value, index) => ({
                        validDate: curve.validDate,
                        name: this.focusedInterestRate.name,
                        termName: this.focusedInterestRate?.gridPoints[index]?.name ?? 'N/A',
                        term: this.focusedInterestRate?.gridPoints[index]?.term ?? 'N/A',
                        value: value / 100000
                    })) ?? [];
                });

                const columns = [
                    this.curvesTableConfig.columns[0], //this.$moment(d.validDate).format('DD.MM.YYYY')
                    this.interestRateTableConfig.columns[0], // d.name
                    this.curvesValuesTableConfig.columns[0], // d.termName
                    this.gridPointsTableConfig.columns[1], // d.term
                    this.curvesValuesTableConfig.columns[1], // d.value

                ]

                await handleTableExport(columns, exportedData, `urokova_mira_${this.focusedInterestRate.name}`);

                this.$modal.hide('exportIntervalModal');
                this.$notify({
                    group: 'application',
                    title: this.$t('exportSuccess'),
                    text: this.$t('exportSuccess'),
                    type: "ok"
                })
            } catch (error) {
                this.$notify({
                    group: 'application',
                    title: this.$t('exportError'),
                    text: this.$t('exportError'),
                    type: "error"
                })
            }

        },
    },
    async mounted() {
        const response = await Axios.get('currency/api/v1/interest-rates?size=10000');
        this.interestRates = response.data;

    }
}
</script>

<style scoped>

</style>